import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Modal, Tab, Nav, Spinner, Alert } from "react-bootstrap";
import { FaWhatsapp, FaInstagram, FaTelegram } from "react-icons/fa";
import { providers } from "../../config/integrations/providers";
import connectionService from "../../services/connectionService";
import ProviderSelector from "./ConnectionWizard/ProviderSelector";
import ConfigTab from "./ConnectionWizard/ConfigTab";
import IntervalsTab from "./ConnectionWizard/IntervalsTab";
import WebhooksTab from "./ConnectionWizard/WebhooksTab";
import TestTab from "./ConnectionWizard/TestTab";

const FORM_INITIAL_STATE = {
  name: "",
  config: {},
  intervals: { automation: 5, attendant: 5, typing: 2 },
  webhooks: { url: "", token: "", events: {}, hmac: false, retry: false },
};

/* ── helpers visuais ── */
const inputBase = {
  width: '100%', padding: '9px 12px', borderRadius: '8px',
  border: '1px solid #e5e7eb', fontSize: `calc(13px * var(--font-scale))`, color: '#374151',
  fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff',
  boxSizing: 'border-box', transition: 'border-color .15s, box-shadow .15s',
};
const fh = {
  onFocus: e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,.1)'; },
  onBlur:  e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; },
};

const CHANNEL_META = {
  whatsapp:  { icon: <FaWhatsapp size={18} color="#25D366" />, bg: '#f0fdf4', border: '#bbf7d0', label: 'WhatsApp' },
  instagram: { icon: <FaInstagram size={18} color="#E1306C" />, bg: '#fdf2f8', border: '#f9a8d4', label: 'Instagram' },
  telegram:  { icon: <FaTelegram size={18} color="#229ED9" />, bg: '#eff6ff', border: '#bfdbfe', label: 'Telegram' },
};

const PrimaryBtn = ({ children, onClick, disabled, loading, color = '#2563eb', small, outline }) => (
  <button
    type="button" onClick={onClick} disabled={disabled || loading}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: '6px',
      padding: small ? '6px 14px' : '9px 20px',
      borderRadius: '8px',
      border: outline ? `1px solid ${color}` : 'none',
      background: outline ? 'transparent' : ((disabled || loading) ? '#93c5fd' : `linear-gradient(135deg,#1e3a5f,${color})`),
      color: outline ? color : '#fff',
      fontSize: small ? '12px' : '13px', fontWeight: '600', fontFamily: 'inherit',
      cursor: (disabled || loading) ? 'not-allowed' : 'pointer',
      transition: 'opacity .15s',
    }}
  >
    {loading && <Spinner as="span" animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />}
    {children}
  </button>
);

const StatusPill = ({ active }) => (
  <span style={{
    fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', padding: '2px 8px', borderRadius: '999px',
    backgroundColor: active ? '#f0fdf4' : '#f9fafb',
    color: active ? '#16a34a' : '#6b7280',
    border: `1px solid ${active ? '#bbf7d0' : '#e5e7eb'}`,
  }}>
    {active ? '● Ativo' : '○ Inativo'}
  </span>
);

const ConexoesTab = () => {
  const [showModal, setShowModal]     = useState(false);
  const [step, setStep]               = useState("select");
  const [search, setSearch]           = useState("");
  const [activeTab, setActiveTab]     = useState("config");
  const [channelKey, setChannelKey]   = useState("whatsapp");
  const [providerKey, setProviderKey] = useState(null);
  const [form, setForm]               = useState(FORM_INITIAL_STATE);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState(null);
  const [editingId, setEditingId]     = useState(null);
  const [connectionLimits, setConnectionLimits] = useState([]);

  const fetchConnections = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [items, limits] = await Promise.all([
        connectionService.list(), connectionService.limits(),
      ]);
      setConnections(items);
      setConnectionLimits(limits);
    }
    catch { setError("Erro ao carregar conexões. Tente novamente."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  const handleSave = async () => {
    setSaving(true); setError(null);
    try {
      const payload = { name: form.name, channel: channelKey, provider: providerKey, config: form.config, intervals: form.intervals, webhooks: form.webhooks };
      if (editingId) {
        const updated = await connectionService.update(editingId, payload);
        setConnections(prev => prev.map(c => c.id === editingId ? updated : c));
      } else {
        const created = await connectionService.create(payload);
        setConnections(prev => [...prev, created]);
      }
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao salvar conexão.");
    } finally { setSaving(false); }
  };

  const toggleConnection = async (id) => {
    try {
      const updated = await connectionService.toggle(id);
      setConnections(prev => prev.map(c => c.id === id ? updated : c));
    } catch { setError("Erro ao alterar status da conexão."); }
  };

  const openNewConnection = () => {
    setEditingId(null); setForm(FORM_INITIAL_STATE); setStep("select");
    setActiveTab("config"); setProviderKey(null); setChannelKey("whatsapp");
    setError(null); setShowModal(true);
  };

  const openEditConnection = (connection) => {
    setEditingId(connection.id); setChannelKey(connection.channel); setProviderKey(connection.provider);
    setForm({ name: connection.name, config: connection.config || {}, intervals: connection.intervals || FORM_INITIAL_STATE.intervals, webhooks: connection.webhooks || FORM_INITIAL_STATE.webhooks });
    setStep("config"); setActiveTab("config"); setError(null); setShowModal(true);
  };

  const currentProvider = providers?.[channelKey]?.providers?.[providerKey];

  const filteredConnections = useMemo(() => {
    if (search.length < 2) return connections;
    return connections.filter(c => c.name.toLowerCase().includes(search.toLowerCase()));
  }, [connections, search]);

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>

      {/* ── HEADER ── */}
      <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="22" height="22" fill="none" stroke="#fff" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
            </svg>
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '800', color: '#fff' }}>Conexões</div>
            <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: 'rgba(255,255,255,.7)' }}>Gerencie integrações de canais de comunicação</div>
          </div>
        </div>
        <PrimaryBtn onClick={openNewConnection} color="#fff" outline>
          <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
          Nova Conexão
        </PrimaryBtn>
      </div>

      {/* ── ERRO GLOBAL ── */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ borderRadius: '10px', fontSize: `calc(13px * var(--font-scale))`, marginBottom: '16px' }}>
          {error}
        </Alert>
      )}

      {/* ── BUSCA ── */}
      <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '14px 16px', marginBottom: '20px' }}>
        <div style={{ position: 'relative' }}>
          <svg width="15" height="15" fill="none" stroke="#9ca3af" viewBox="0 0 24 24" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            style={{ ...inputBase, paddingLeft: '36px' }}
            placeholder="Pesquisar conexões..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            {...fh}
          />
        </div>
      </div>

      {/* ── LOADING ── */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: '12px' }}>
          <Spinner animation="border" variant="primary" style={{ width: '28px', height: '28px' }} />
          <span style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280' }}>Carregando conexões...</span>
        </div>
      )}

      {/* ── GRID DE CARDS ── */}
      {!loading && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '14px' }}>

          {filteredConnections.map(connection => {
            const meta = CHANNEL_META[connection.channel] || {};
            return (
              <div
                key={connection.id}
                onClick={() => openEditConnection(connection)}
                style={{
                  backgroundColor: '#fff', borderRadius: '14px', border: `1px solid ${connection.is_active ? meta.border || '#e5e7eb' : '#e5e7eb'}`,
                  padding: '18px', cursor: 'pointer', transition: 'box-shadow .2s, transform .15s',
                  boxShadow: '0 1px 4px rgba(0,0,0,.06)',
                }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 20px rgba(37,99,235,.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '38px', height: '38px', borderRadius: '10px', backgroundColor: meta.bg || '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${meta.border || '#e5e7eb'}` }}>
                      {meta.icon}
                    </div>
                    <div>
                      <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', color: '#111827' }}>{connection.name}</div>
                      <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>{connection.provider}</div>
                    </div>
                  </div>

                  {/* Toggle — stopPropagation para não abrir o modal */}
                  <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ position: 'relative', display: 'inline-block', width: '36px', height: '20px', cursor: 'pointer' }}>
                      <input
                        type="checkbox" checked={!!connection.is_active}
                        onChange={() => toggleConnection(connection.id)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute', inset: 0, borderRadius: '999px', transition: '.2s',
                        backgroundColor: connection.is_active ? '#16a34a' : '#d1d5db',
                      }} />
                      <span style={{
                        position: 'absolute', left: connection.is_active ? '18px' : '2px', top: '2px',
                        width: '16px', height: '16px', borderRadius: '50%', backgroundColor: '#fff',
                        transition: '.2s', boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                      }} />
                    </label>
                  </div>
                </div>

                <StatusPill active={connection.is_active} />
              </div>
            );
          })}

          {/* Card "Nova Conexão" */}
          <div
            onClick={openNewConnection}
            style={{
              backgroundColor: '#f8fafc', borderRadius: '14px', border: '2px dashed #cbd5e1',
              padding: '18px', cursor: 'pointer', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '110px',
              transition: 'border-color .2s, background .2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.style.backgroundColor = '#eff6ff'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.backgroundColor = '#f8fafc'; }}
          >
            <div style={{ width: '36px', height: '36px', borderRadius: '10px', backgroundColor: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="18" height="18" fill="none" stroke="#2563eb" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <span style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', color: '#2563eb' }}>Nova Conexão</span>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════
          MODAL
      ══════════════════════════════════ */}
      <style>{`
        .cm-modal .modal-content { border-radius: 16px !important; border: none !important; overflow: hidden; font-family: 'Inter','Segoe UI',sans-serif; box-shadow: 0 25px 60px rgba(0,0,0,.18) !important; }
        .cm-modal .modal-header { background: linear-gradient(135deg,#1e3a5f,#2563eb); border: none !important; padding: 18px 22px !important; }
        .cm-modal .modal-body { padding: 22px !important; background: #f8fafc; }
        .cm-modal .modal-footer { background: #f1f5f9; border-top: 1px solid #e5e7eb !important; padding: 12px 22px !important; }
        .cm-modal .btn-close { filter: invert(1) brightness(2) !important; }
        .cm-nav .nav-link { font-size: calc(13px * var(--font-scale)) font-weight: 600; color: #6b7280; border-radius: 8px 8px 0 0 !important; padding: 8px 16px !important; }
        .cm-nav .nav-link.active { color: #2563eb !important; border-bottom-color: #fff !important; }
      `}</style>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" backdrop="static" centered dialogClassName="cm-modal">
        <Modal.Header closeButton>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '9px', backgroundColor: 'rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="17" height="17" fill="none" stroke="#fff" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
              </svg>
            </div>
            <div>
              <div style={{ fontSize: `calc(14px * var(--font-scale))`, fontWeight: '700', color: '#fff' }}>{editingId ? 'Editar Conexão' : 'Nova Conexão'}</div>
              <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: 'rgba(255,255,255,.7)' }}>Configure o canal de comunicação</div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)} style={{ borderRadius: '10px', fontSize: `calc(13px * var(--font-scale))`, marginBottom: '16px' }}>
              {error}
            </Alert>
          )}

          {step === "select" ? (
            <ProviderSelector
              providers={providers} channelKey={channelKey} setChannelKey={setChannelKey}
              providerKey={providerKey} setProviderKey={setProviderKey}
              limits={connectionLimits}
              onBlocked={limit => setError(
                `Limite atingido: ${limit.used} de ${limit.limit} conexão(ões) para este provedor. Contrate uma conexão adicional em Plano e Assinatura.`
              )}
              onNext={() => setStep("config")}
            />
          ) : (
            <>
              {/* Breadcrumb do provider */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', padding: '10px 14px', marginBottom: '18px' }}>
                {CHANNEL_META[channelKey]?.icon}
                <span style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', color: '#374151' }}>{providers[channelKey]?.label}</span>
                <svg width="14" height="14" fill="none" stroke="#9ca3af" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span style={{ fontSize: `calc(13px * var(--font-scale))`, color: '#6b7280' }}>{currentProvider?.label}</span>
              </div>

              <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                <Nav variant="tabs" className="cm-nav">
                  {[
                    { key: 'config',    label: channelKey === 'instagram' ? 'Dados do Instagram' : 'Configuração' },
                    { key: 'intervals', label: 'Intervalos' },
                    ...(channelKey !== 'instagram' ? [{ key: 'webhooks', label: 'Webhooks' }] : []),
                    ...(editingId ? [{ key: 'test', label: 'Teste' }] : []),
                  ].map(t => (
                    <Nav.Item key={t.key}>
                      <Nav.Link eventKey={t.key}>{t.label}</Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <div style={{ backgroundColor: '#fff', borderRadius: '0 0 12px 12px', border: '1px solid #e5e7eb', borderTop: 'none', padding: '20px' }}>
                  <Tab.Content>
                    <Tab.Pane eventKey="config">
                      <ConfigTab
                        form={form}
                        setForm={setForm}
                        provider={currentProvider}
                        channel={channelKey}
                        connectionId={editingId}
                        onConnectionChanged={async (connectionId) => {
                          if (connectionId && form.name.trim()) {
                            await connectionService.update(connectionId, {
                              name: form.name.trim(),
                            });
                          }

                          await fetchConnections();
                          setShowModal(false);
                        }}
                      />
                    </Tab.Pane>
                    <Tab.Pane eventKey="intervals">
                      <IntervalsTab form={form} setForm={setForm} />
                    </Tab.Pane>
                    {channelKey !== 'instagram' && (
                      <Tab.Pane eventKey="webhooks">
                        <WebhooksTab
                          form={form}
                          setForm={setForm}
                          channel={channelKey}
                          provider={providerKey}
                        />
                      </Tab.Pane>
                    )}
                    {editingId && (
                      <Tab.Pane eventKey="test">
                        <TestTab
                          connectionId={editingId}
                          form={form}
                          channel={channelKey}
                          onConnectionTested={fetchConnections}
                        />
                      </Tab.Pane>
                    )}
                  </Tab.Content>
                </div>
              </Tab.Container>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {step === "config" && !editingId && (
              <PrimaryBtn outline color="#6b7280" onClick={() => setStep("select")}>
                <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Voltar
              </PrimaryBtn>
            )}
            <button
              type="button" onClick={() => setShowModal(false)} disabled={saving}
              style={{ padding: '8px 18px', borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
            >
              Cancelar
            </button>
            {step === "config" && (channelKey !== "instagram" || editingId) && (
              <PrimaryBtn onClick={handleSave} disabled={saving} loading={saving} color="#16a34a">
                <svg width="13" height="13" fill="none" stroke="#fff" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
                {saving ? 'Salvando...' : editingId ? 'Atualizar Conexão' : 'Salvar Conexão'}
              </PrimaryBtn>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ConexoesTab;
